import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2 } from "lucide-react";
import { RESOURCE_CONFIGS } from "@/admin/resourceConfigs";
import { Field } from "@/admin/FormFields";
import { adminList, adminCreate, adminUpdate, adminDelete } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ResourceManager() {
  const { resource } = useParams();
  const config = RESOURCE_CONFIGS[resource];
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // object or null
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  React.useEffect(() => { setPage(1); }, [resource]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin", resource],
    queryFn: () => adminList(resource),
    enabled: !!config,
  });

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const saveMut = useMutation({
    mutationFn: (data) => (data.id ? adminUpdate(resource, data.id, data) : adminCreate(resource, data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", resource] });
      toast.success("Saved successfully");
      setEditing(null);
    },
    onError: () => toast.error("Failed to save"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => adminDelete(resource, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", resource] });
      toast.success("Deleted");
      setDeleteId(null);
    },
  });

  if (!config) return <p className="p-8 text-slate-500">Unknown resource.</p>;

  const startNew = () => {
    const obj = {};
    config.fields.forEach((f) => { if (f.default !== undefined) obj[f.key] = f.default; });
    setEditing(obj);
  };

  const validate = (data) => {
    for (const f of config.fields) {
      if (f.required && !data[f.key]) {
        toast.error(`${f.label} is required`);
        return false;
      }
    }
    return true;
  };

  return (
    <div data-testid={`resource-${resource}`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">{config.title}</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} item(s)</p>
        </div>
        <button onClick={startNew} data-testid="add-new-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white font-semibold text-sm hover:bg-navy-700 transition-colors">
          <Plus className="h-4 w-4" /> Add {config.singular}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {config.columns.map((c) => (
                    <th key={c} className="text-left px-5 py-3 font-semibold text-slate-600 capitalize">{c}</th>
                  ))}
                  <th className="px-5 py-3 text-right font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={config.columns.length + 1} className="px-5 py-12 text-center text-slate-400">No items yet. Click "Add {config.singular}".</td></tr>
                )}
                {pageItems.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    {config.columns.map((c) => (
                      <td key={c} className="px-5 py-3 text-slate-700 max-w-xs truncate">{String(it[c] ?? "—")}</td>
                    ))}
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setEditing(it)} data-testid={`edit-${it.id}`}
                          className="h-9 w-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-navy hover:text-white transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setDeleteId(it.id)} data-testid={`delete-${it.id}`}
                          className="h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50" data-testid="resource-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-navy disabled:opacity-40 hover:bg-white">← Prev</button>
              <span className="text-xs text-slate-500 font-mono">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-navy disabled:opacity-40 hover:bg-white">Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Edit/Create drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="resource-form-drawer">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-display text-xl font-semibold text-navy">
                {editing.id ? `Edit ${config.singular}` : `New ${config.singular}`}
              </h2>
              <button onClick={() => setEditing(null)} className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {config.fields.map((f) => (
                <Field key={f.key} field={f} value={editing[f.key]} onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))} />
              ))}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
              <button onClick={() => setEditing(null)} className="flex-1 px-5 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold">Cancel</button>
              <button onClick={() => validate(editing) && saveMut.mutate(editing)} disabled={saveMut.isPending} data-testid="save-resource-button"
                className="flex-1 px-5 py-3 rounded-xl bg-gold text-navy font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMut.mutate(deleteId)} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
